import Button from '@/components/Buttons/Button';
import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import CheckCircle from '@/images/CheckCircle.svg';
import Trash from '@/images/Trash.svg';
import { useCreateAvatarStore } from '@/pages/avatar/common/context';
import { AvatarType } from '@/pages/avatar/common/types';
import { useFlagExperiment } from '@/services/featureFlags';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import FootageSafeZoneToggle from './FootageSafeZoneToggle';
import { GreenScreenSelection } from './GreenScreenSelection';
import { IdleVideoUpload } from './IdleVideoUpload';
import { FootageRequirement, SAFE_ZONE_FACE_VISIBLE_TEXT, requirementsMap } from './requirements';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    width: 550px;
    height: calc(100% - 96px);
`;

const VideoContainer = styled.div`
    border-radius: 8px;
    position: relative;
    height: calc(100% - 314px);
`;

const SafeZoneBorders = styled.div<{ isSafeZone: boolean }>`
    position: relative;
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;

    ${({ theme, isSafeZone }) =>
        isSafeZone &&
        css`
            &::before,
            &::after {
                content: '';
                position: absolute;
                top: 0;
                bottom: 0;
                width: 0;
                border-left: 4px dashed ${theme.colors.warn[500]};
                z-index: 2;
            }

            &::before {
                left: 25%;
            }

            &::after {
                left: 75%;
            }
        `}
`;

const Video = styled.video`
    border-radius: 8px;
    width: 100%;
    max-height: 100%;
`;

const RequirementsTitle = styled.span`
    color: ${({ theme }) => theme.colors.secondary[900]};
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.4px;
    text-align: center;
`;

const RequirementsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Requirement = styled.div<{ isConfirmed: boolean }>`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 20px;
    background-color: ${({ theme, isConfirmed }) => (isConfirmed ? '#E7FFE7' : theme.colors.black[100])};
    border-radius: 16px;

    color: ${({ theme }) => theme.colors.secondary[900]};
    font-size: 18px;
    font-weight: 600;
    letter-spacing: -0.36px;

    span {
        margin-right: auto;
    }
`;

const IconCircle = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background-color: white;
`;

const StyledIconButton = styled(IconButton)`
    position: absolute;
    background-color: white;
    top: 12px;
    right: 12px;
    padding: 6px;

    border: 1px solid ${({ theme }) => theme.colors.black[300]};
    border-radius: 8px;

    :hover {
        background-color: ${({ theme }) => theme.colors.black[100]};
        border: 1px solid ${({ theme }) => theme.colors.black[300]};
        border-radius: 8px;
    }
`;

export const ReviewFootage = observer(() => {
    const theme = useTheme();
    const createAvatarStore = useCreateAvatarStore();
    const avatarType = createAvatarStore.type;
    const [isSafeZoneEnabled, setSafeZoneEnabled] = useState<boolean>(false);
    const greenscreenEnabled = useFlagExperiment('is_enabled_dvp_greenscreen', false);
    const idleVideoEnabled = useFlagExperiment('is_enabled_dvp_idle_video', false);

    const handleRetry = () => {
        createAvatarStore.resetFootageUpload();
    };

    const handleConfirm = (idx: number) => {
        createAvatarStore.toggleConfirmation(idx);
    };

    return (
        <Container>
            <VideoContainer>
                <SafeZoneBorders isSafeZone={isSafeZoneEnabled}>
                    <Video controls key={createAvatarStore.footageSrc}>
                        <source src={createAvatarStore.footageSrc} />
                    </Video>
                </SafeZoneBorders>
                <StyledIconButton ariaLabel="replace video" onClick={handleRetry}>
                    <MaskedIcon src={Trash} size="18px" color={theme.colors.secondary[900]} />
                </StyledIconButton>
                {avatarType === AvatarType.PremiumPlus && (
                    <FootageSafeZoneToggle isSafeZoneEnabled={isSafeZoneEnabled} setSafeZoneEnabled={setSafeZoneEnabled} />
                )}
            </VideoContainer>

            <RequirementsContainer>
                {(avatarType === AvatarType.PremiumPlus || avatarType === AvatarType.Express) && greenscreenEnabled && (
                    <GreenScreenSelection />
                )}
                {avatarType === AvatarType.PremiumPlus && idleVideoEnabled && <IdleVideoUpload />}
                <RequirementsTitle>Check requirements</RequirementsTitle>
                {createAvatarStore.footageRequirements
                    .filter(requirement => {
                        return !requirement.avatarType || requirement.avatarType === avatarType;
                    })
                    .map((requirement, index) => {
                        const isAlreadyConfirmed = createAvatarStore.footageRequirements[index].confirmed;
                        const previousIsConfirmed = index > 0 ? createAvatarStore.footageRequirements[index - 1].confirmed : true;

                        return (
                            <Requirement
                                key={index}
                                isConfirmed={createAvatarStore.footageRequirements[index].confirmed}
                                data-testid={requirement.testId}>
                                <IconCircle>
                                    <MaskedIcon src={requirement.icon} size="24px" color={theme.colors.secondary[900]} />
                                </IconCircle>
                                <span>
                                    {isSafeZoneEnabled && requirement.text.includes(requirementsMap[FootageRequirement.FACE_VISIBLE].text)
                                        ? SAFE_ZONE_FACE_VISIBLE_TEXT
                                        : requirement.text}
                                </span>

                                {!isAlreadyConfirmed && previousIsConfirmed && (
                                    <Button variant="contained" color="secondary" onClick={() => handleConfirm(index)}>
                                        Confirm
                                    </Button>
                                )}

                                {isAlreadyConfirmed && <MaskedIcon src={CheckCircle} size="32px" color={theme.colors.success[550]} />}
                            </Requirement>
                        );
                    })}
            </RequirementsContainer>
        </Container>
    );
});
